import { AccountModel, AccountProfileLinkType, AccountProfileModel, AccountType } from './accounts';
import {
  ActivationOperatorTokenModel,
  ActivationOperatorType,
  ActivationParentType,
  ActivationPhraseModel,
  ActivationPhraseScopeType,
  ActivationPhraseTemplateModel,
  ActivationPhraseTemplateType,
  ActivationRuleItemType,
  ActivationRuleModel,
  ActivationTagTokenModel,
  ActivationTaskTokenModel,
  ActivationTextTokenModel,
  ActivationTokenOperatorType,
  ActivationTokenType,
  ActivationTokenValueType,
  ActivationValueTokenModel,
  ActivationVariableTokenModel,
  CreateOrUpdateActivationRuleModel,
  getActivationTokenDefaultValue,
  getActivationTokenLabel
} from './activations';
import {
  AssignmentBlockOutputLogModel,
  AssignmentBlockOutputResultModel,
  AssignmentBlockUserInputModel,
  AssignmentModel,
  AssignmentTaskSlideType,
  AssignmentType,
  AssignmentsResponseModel,
  GetAssignmentsParamsModel,
  TaskAssignmentCreateModel,
  UserInputType
} from './assignments';
import { PermissionEntityType, PermissionModel, PermissionType } from './authorization';
import {
  ChatbotDataEntryModel,
  ChatbotDataEntryType,
  ChatbotSettingsModel,
  ChatbotTestRequestModel
} from './chatbots';
import {
  CreateEntityResponseModel,
  CreateResponseModel,
  SuccessResponseModel,
  ToastModel,
  ToastType
} from './common';
import { GarminConnectionModel, GarminConnectionStatus } from './connections';
import {
  ConversationModel,
  ConversationParticipantModel,
  GetAttachmentsParamModel,
  MessageAttachmentModel,
  MessageAttachmentTaskResultModel,
  MessageAttachmentType,
  MessageContextMenuVersion,
  MessageModel,
  MessageRecommendationStatus,
  MessageReplyType,
  MessageResponseRecommendationModel,
  MessageSenderFeedbackModel
} from './conversations';
import {
  CreatedGoalSubscriptionModel,
  GetGoalSubscriptionsParamsModel,
  GoalSubscriptionGetFiltersModel,
  GoalSubscriptionModel,
  GoalSubscriptionPriority,
  GoalSubscriptionProgressElementModel,
  GoalSubscriptionStatus,
  GoalSubscriptionTab,
  GoalSubscriptionsSummaryModel
} from './goalSubscriptions';
import {
  GetGoalTOCParamsModel,
  GoalTOCGoalSectionModel,
  GoalTOCModel,
  GoalTOCStrategySectionModel,
  GoalTOCStrategySectionTaskModel
} from './goalTocs';
import {
  ComboGoalGraphModel,
  GoalCardLinkTarget,
  GoalChildrenSummaryModel,
  GoalGetFiltersModel,
  GoalModel,
  GoalPatchModel,
  GoalPriceDetailsModel,
  GoalPriceModel,
  GoalPricePatchModel,
  GoalType,
  GoalVisibilityStatus
} from './goals';
import { MasterHelloModel } from './master';
import {
  CreateMediaitemModel,
  MediaitemFileModel,
  MediaitemFileQuality,
  MediaitemFileRole,
  MediaitemModel,
  MediaitemSignedUploadModel,
  MediaitemType,
  MediaitemUsageType
} from './mediaitems';
import {
  AssignmentSchedulingJobStatus,
  AssignmentSchedulingJobStatusModel,
  AvailabilityModel,
  CommandExecutionModel,
  GetAvailabilitiesParamsModel,
  ScheduleCommand
} from './schedules';
import { DisplayMode, SuccessCreatedEntity, Unit, UnitNamesIndex } from './shared';
import { AssignmentBlockOutputResponseModel, AssignmentTaskStateModel } from './state';
import {
  CreateNodesBatchModel,
  CreateOrUpdateStrategyNodeModel,
  CreateOrUpdateStrategyPathModel,
  NodeModel,
  NodeType,
  PathModel,
  PathType,
  StrategyModel,
  createEmptyNode
} from './strategies';
import { SupplyModel, SupplyQuantityModel } from './supplies';
import { TagModel, TagType } from './tags';
import {
  BlankContentModel,
  BlankModel,
  BlankOccurrenceModel,
  BlankProblemModel,
  BlockAssertionCompareType,
  BlockAssertionModel,
  BlockAssertionSpecModel,
  BlockAssertionStepType,
  BlockAssertionType,
  BlockAssertionValueModel,
  BlockAssertionValueType,
  BlockBlanksDragTypes,
  BlockBooleanType,
  BlockBrickCategoryModel,
  BlockBrickModel,
  BlockBricksDragTypes,
  BlockCategory,
  BlockContentChoiceModel,
  BlockCreateModel,
  BlockExerciseModel,
  BlockGenericModel,
  BlockGroupContentModel,
  BlockGroupType,
  BlockGroupUnit,
  BlockInputBooleanContentModel,
  BlockInputBricksContentModel,
  BlockInputChoicesContentModel,
  BlockInputDateContentModel,
  BlockInputDatetimeContentModel,
  BlockInputFloatContentModel,
  BlockInputIntegerContentModel,
  BlockInputRatingContentModel,
  BlockInputTextContentModel,
  BlockInputTimeContentModel,
  BlockInputTimerContentModel,
  BlockInputTrainingContentModel,
  BlockIntensity,
  BlockModel,
  BlockStaticContentModel,
  BlockType,
  BlockUpdateModel,
  BrickAssociationsType,
  ContentItemModality,
  ContentItemModel,
  DraggedBlankModel,
  DraggedBrickModel,
  TaskCreateModel,
  TaskModel,
  TaskUpdateModel
} from './tasks';
import { UserModel } from './users';
import { VersionedEntityOperationType, VersionedEntityType } from './versions';

class ModelsFactory {
  static emptyGoalPatch = (): GoalPatchModel => {
    return {
      title: '',
      shortDescription: '',
      description: ''
    };
  };
}

export {
  AccountProfileLinkType,
  AccountType,
  ActivationOperatorType,
  ActivationParentType,
  ActivationPhraseScopeType,
  ActivationPhraseTemplateType,
  ActivationRuleItemType,
  ActivationTokenOperatorType,
  ActivationTokenType,
  ActivationTokenValueType,
  AssignmentSchedulingJobStatus,
  AssignmentTaskSlideType,
  AssignmentType,
  BlockAssertionCompareType,
  BlockAssertionStepType,
  BlockAssertionType,
  BlockAssertionValueType,
  BlockBlanksDragTypes,
  BlockBooleanType,
  BlockBricksDragTypes,
  BlockCategory,
  BlockGroupType,
  BlockGroupUnit,
  BlockIntensity,
  BlockType,
  ChatbotDataEntryType,
  ContentItemModality,
  DisplayMode,
  GarminConnectionStatus,
  GoalCardLinkTarget,
  GoalSubscriptionPriority,
  GoalSubscriptionStatus,
  GoalSubscriptionTab,
  GoalType,
  GoalVisibilityStatus,
  MediaitemFileQuality,
  MediaitemFileRole,
  MediaitemType,
  MediaitemUsageType,
  MessageAttachmentType,
  MessageContextMenuVersion,
  MessageRecommendationStatus,
  MessageReplyType,
  ModelsFactory,
  NodeType,
  PathType,
  PermissionEntityType,
  PermissionType,
  ScheduleCommand,
  TagType,
  ToastType,
  Unit,
  UnitNamesIndex,
  UserInputType,
  VersionedEntityOperationType,
  VersionedEntityType,
  createEmptyNode,
  getActivationTokenDefaultValue,
  getActivationTokenLabel
};
export type {
  AccountModel,
  AccountProfileModel,
  ActivationOperatorTokenModel,
  ActivationPhraseModel,
  ActivationPhraseTemplateModel,
  ActivationRuleModel,
  ActivationTagTokenModel,
  ActivationTaskTokenModel,
  ActivationTextTokenModel,
  ActivationValueTokenModel,
  ActivationVariableTokenModel,
  AssignmentBlockOutputLogModel,
  AssignmentBlockOutputResponseModel,
  AssignmentBlockOutputResultModel,
  AssignmentBlockUserInputModel,
  AssignmentModel,
  AssignmentSchedulingJobStatusModel,
  AssignmentTaskStateModel,
  AssignmentsResponseModel,
  AvailabilityModel,
  BlankContentModel,
  BlankModel,
  BlankOccurrenceModel,
  BlankProblemModel,
  BlockAssertionModel,
  BlockAssertionSpecModel,
  BlockAssertionValueModel,
  BlockBrickCategoryModel,
  BlockBrickModel,
  BlockContentChoiceModel,
  BlockCreateModel,
  BlockExerciseModel,
  BlockGenericModel,
  BlockGroupContentModel,
  BlockInputBooleanContentModel,
  BlockInputBricksContentModel,
  BlockInputChoicesContentModel,
  BlockInputDateContentModel,
  BlockInputDatetimeContentModel,
  BlockInputFloatContentModel,
  BlockInputIntegerContentModel,
  BlockInputRatingContentModel,
  BlockInputTextContentModel,
  BlockInputTimeContentModel,
  BlockInputTimerContentModel,
  BlockInputTrainingContentModel,
  BlockModel,
  BlockStaticContentModel,
  BlockUpdateModel,
  BrickAssociationsType,
  ChatbotDataEntryModel,
  ChatbotSettingsModel,
  ChatbotTestRequestModel,
  ComboGoalGraphModel,
  CommandExecutionModel,
  ContentItemModel,
  ConversationModel,
  ConversationParticipantModel,
  CreateEntityResponseModel,
  CreateMediaitemModel,
  CreateNodesBatchModel,
  CreateOrUpdateActivationRuleModel,
  CreateOrUpdateStrategyNodeModel,
  CreateOrUpdateStrategyPathModel,
  CreateResponseModel,
  CreatedGoalSubscriptionModel,
  DraggedBlankModel,
  DraggedBrickModel,
  GarminConnectionModel,
  GetAssignmentsParamsModel,
  GetAttachmentsParamModel,
  GetAvailabilitiesParamsModel,
  GetGoalSubscriptionsParamsModel,
  GetGoalTOCParamsModel,
  GoalChildrenSummaryModel,
  GoalGetFiltersModel,
  GoalModel,
  GoalPatchModel,
  GoalPriceDetailsModel,
  GoalPriceModel,
  GoalPricePatchModel,
  GoalSubscriptionGetFiltersModel,
  GoalSubscriptionModel,
  GoalSubscriptionProgressElementModel,
  GoalSubscriptionsSummaryModel,
  GoalTOCGoalSectionModel,
  GoalTOCModel,
  GoalTOCStrategySectionModel,
  GoalTOCStrategySectionTaskModel,
  MasterHelloModel,
  MediaitemFileModel,
  MediaitemModel,
  MediaitemSignedUploadModel,
  MessageAttachmentModel,
  MessageAttachmentTaskResultModel,
  MessageModel,
  MessageResponseRecommendationModel,
  MessageSenderFeedbackModel,
  NodeModel,
  PathModel,
  PermissionModel,
  StrategyModel,
  SuccessCreatedEntity,
  SuccessResponseModel,
  SupplyModel,
  SupplyQuantityModel,
  TagModel,
  TaskAssignmentCreateModel,
  TaskCreateModel,
  TaskModel,
  TaskUpdateModel,
  ToastModel,
  UserModel
};
